import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  QrCode, LayoutTemplate, ClipboardList, Ticket, Users as UsersIcon,
  Package, Megaphone, BarChart3, FileText, Gift, UserCircle2, DollarSign,
  ArrowRight, Building2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOLS = [
  { key: 'check_in', title: 'Check-In & Scanning', icon: QrCode, href: '/team/checkin', desc: 'Scan tickets at the door.' },
  { key: 'page_builder', title: 'Event Page Builder', icon: LayoutTemplate, href: '/admin/events', desc: 'Customize landing pages.' },
  { key: 'production_manager', title: 'Production Manager', icon: ClipboardList, href: '/admin/events', desc: 'Tasks, run-of-show, budget.' },
  { key: 'ticketing', title: 'Ticketing', icon: Ticket, href: '/admin/events', desc: 'Tickets, promo codes, fees.' },
  { key: 'staffing', title: 'Staffing & Jobs', icon: UsersIcon, href: '/staffing', desc: 'Post jobs, review applicants.' },
  { key: 'vendors_rentals', title: 'Vendors & Rentals', icon: Package, href: '/admin/rentals', desc: 'Manage supplier orders.' },
  { key: 'marketing', title: 'Marketing & Ads', icon: Megaphone, href: '/admin/marketing', desc: 'Campaigns and blasts.' },
  { key: 'analytics', title: 'Analytics', icon: BarChart3, href: '/admin/analytics', desc: 'Sales, forecasts, Pulse.' },
  { key: 'venue_proposals', title: 'Venue Proposals', icon: FileText, href: '/admin/events', desc: 'Pitch venues with pro-forma.' },
  { key: 'swag_sponsorships', title: 'Swag & Sponsors', icon: Gift, href: '/admin/events', desc: 'Swag bags and sponsors.' },
  { key: 'crm', title: 'Attendee CRM', icon: UserCircle2, href: '/admin/ticket-holders', desc: 'Ticket holder data.' },
  { key: 'finance', title: 'Finance', icon: DollarSign, href: '/admin/analytics', desc: 'Payouts and refunds.' },
];

export default function TeamPortal() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/team/me`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
        // If user is an admin, they shouldn't be on team portal
        if (user?.role === 'admin') navigate('/admin');
      } catch (e) {
        setData({ memberships: [], merged_permissions: {}, is_team_member: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [token, user, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading team portal…</div>;
  }

  if (!data?.is_team_member) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">No team access yet</h2>
            <p className="text-sm text-muted-foreground mb-6">You haven't been invited to manage any events. Once an organizer invites you, your tools will appear here.</p>
            <Link to="/" className="text-sm text-indigo-600 hover:underline">Go to homepage</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const perms = data.merged_permissions || {};
  const allowed = TOOLS.filter(t => perms[t.key]);
  const orgCount = data.memberships?.length || 0;

  return (
    <div className="min-h-screen bg-background py-10 px-4 sm:px-6 lg:px-8" data-testid="team-portal-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <Badge variant="outline" className="mb-3">Team Portal</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">Welcome, {user?.name || user?.email?.split('@')[0]}</h1>
          <p className="text-muted-foreground">You have access to {allowed.length} tool{allowed.length !== 1 ? 's' : ''} across {orgCount} organization{orgCount !== 1 ? 's' : ''}.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-tools-grid">
          {allowed.map(t => {
            const Icon = t.icon;
            return (
              <Link key={t.key} to={t.href} data-testid={`team-tool-${t.key}`}>
                <Card className="h-full hover:border-indigo-500 transition cursor-pointer group">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
                    </div>
                    <div className="font-semibold mb-1">{t.title}</div>
                    <div className="text-sm text-muted-foreground">{t.desc}</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {data.memberships?.length > 0 && (
          <div className="mt-12">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Your Access</h3>
            <div className="grid gap-3">
              {data.memberships.map(m => (
                <div key={m.member_id} className="p-4 rounded-lg border border-border bg-card text-sm">
                  <div className="font-medium mb-1">Invited by {m.email ? <span className="text-muted-foreground">{m.email}</span> : 'Organizer'}</div>
                  <div className="text-xs text-muted-foreground">
                    Scope: {m.scope_type === 'all' ? 'All events' : [
                      m.scope_values?.event_ids?.length && `${m.scope_values.event_ids.length} events`,
                      m.scope_values?.categories?.length && `categories: ${m.scope_values.categories.join(', ')}`,
                      m.scope_values?.tags?.length && `tags: ${m.scope_values.tags.join(', ')}`,
                    ].filter(Boolean).join(' + ') || 'No events'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
